// OFF-C-02 / OFF-C-03 (ADR-128): the Seller's sale and collection go through here. Online they post at once; without
// signal they wait in the outbox on the phone and the Seller gets a "Pending-n" result to carry on with. Back online the
// queue is sent in order; the server re-validates each command and a refusal holds the queue for the Seller.
import { useSyncExternalStore } from 'react';
import { Outbox, type OutboxItem, type SendResult } from '../lib/offline/outbox';
import { IdbStore } from '../lib/offline/idb';
import { Refusal } from './auth';
import { reportCommand } from '../lib/ux/feedback';
import { newCommandId, sendCommand } from './commands';

export const outbox = new Outbox(
  typeof indexedDB === 'undefined'
    ? { all: async () => [], put: async () => {}, remove: async () => {} }
    : new IdbStore(),
);
let loaded: Promise<void> | null = null;
export const ready = () => (loaded ??= outbox.load());
/** Called with the signed-in user: the queue becomes theirs (and sending starts for them). */
export async function setOutboxUser(user: string): Promise<void> {
  loaded = outbox.setUser(user);
  await loaded;
}

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/** Posts at once, or queues when there is no signal. `meta` is what the queue and the pending result show. */
export async function sendOrQueue<T>(
  operation: string,
  params: Record<string, unknown>,
  meta: { kind: 'sale' | 'collection'; customer: string; total: number },
  pendingResult: (pending: number, id: string) => T,
): Promise<T> {
  await ready();
  const id = newCommandId();
  if (!isOffline() && !outbox.held() && outbox.list().length === 0) {
    try {
      return await sendCommand<T>(operation, params, id, true);
    } catch (e) {
      if (!(e instanceof Refusal && e.code === 'OFFLINE')) throw e;
    }
  }
  // No signal, or items already wait (they go first, in order): this one joins the queue under the same command id.
  const item = await outbox.enqueue({ id, operation, params, ...meta });
  reportCommand({ ok: true, operation: 'offline_queued', result: { number: `Pending-${item.pending}` } });
  return pendingResult(item.pending, item.id);
}

export async function sendItem(item: OutboxItem): Promise<SendResult> {
  try {
    await sendCommand(item.operation, item.params, item.id, true);
    return { ok: true };
  } catch (e) {
    if (e instanceof Refusal) {
      if (e.code === 'OFFLINE') return { ok: false, offline: true };
      // A server fault is not the Seller's to fix: keep the item waiting and try again later.
      if (e.code === 'SERVER_ERROR') return { ok: false, offline: true };
      return { ok: false, offline: false, code: e.code, detail: e.detail };
    }
    return { ok: false, offline: true };
  }
}

/** Sends what waits, if there is signal. Returns how many went out. */
export async function flushOutbox(): Promise<number> {
  await ready();
  if (isOffline()) return 0;
  const sent = await outbox.flush(sendItem);
  if (sent > 0) reportCommand({ ok: true, operation: 'offline_sync', result: { number: String(sent) } });
  return sent;
}

export interface OutboxView {
  items: readonly OutboxItem[];
  offline: boolean;
  overdue: boolean;
  held: OutboxItem | undefined;
}
let version = 0;
let online = typeof navigator === 'undefined' ? true : navigator.onLine;
outbox.subscribe(() => (version += 1));
const listeners = new Set<() => void>();
const ping = () => listeners.forEach((l) => l());
outbox.subscribe(ping);
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    online = true;
    ping();
    void flushOutbox();
  });
  window.addEventListener('offline', () => {
    online = false;
    ping();
  });
  // A weak signal can pass the browser's "online" check: try again every half minute while something waits.
  setInterval(() => {
    if (outbox.list().length > 0) void flushOutbox();
  }, 30_000);
}
let snapshot: { v: number; online: boolean; view: OutboxView } | null = null;
function view(): OutboxView {
  if (!snapshot || snapshot.v !== version || snapshot.online !== online) {
    snapshot = {
      v: version,
      online,
      view: { items: outbox.list(), offline: !online, overdue: outbox.overdue(), held: outbox.held() },
    };
  }
  return snapshot.view;
}

/** The queue and the signal, live (the banner, the chip and the queue page read this). */
export function useOutbox(): OutboxView {
  void ready().then(ping);
  return useSyncExternalStore((fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, view);
}

/** A refused sale taken back for editing: the round page reopens the sale sheet with its lines (ADR-128). */
let resume: { customer: string; lines: unknown[] } | null = null;
export const setResume = (r: { customer: string; lines: unknown[] }) => {
  resume = r;
};
export const peekResume = () => resume !== null;
export function takeResume() {
  const r = resume;
  resume = null;
  return r;
}
